/**
 * Computes a hex-encoded SHA-256 digest of the given input using the Web Crypto
 * API. Used to pseudonymize PII (e.g. email addresses) before sending it to
 * third-party analytics, so we get stable de-duplication without storing raw
 * personal data outside our boundary.
 *
 * Returns an empty string for empty/blank input, and `null` if Web Crypto is
 * unavailable (e.g. non-secure contexts) so callers can omit the property.
 */
export const sha256Hex = async (input: string | null | undefined): Promise<string | null> => {
  const value = typeof input === "string" ? input.trim().toLowerCase() : "";
  if (!value) return "";
  if (!globalThis.crypto?.subtle) return null;

  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, "0"))
    .join("");
};
